import { FC, useMemo } from "react";
import { getSegmentNameFromRange } from "../../../Workspace/View/MarketLandscape/utils";
import { pluralize } from "../../../../utils/text";
import { ICPDistributionChart } from "./ICPDistributionChart";
import { DEFAULT_API_EMPLOYEE_RANGES } from "../../../Workspace/View/constants";

interface EmployeeCountDistributionItem {
  share: number;
  count: number;
  isSignificant: boolean;
  employeesRange: string;
  employeesRangeMin: number | null;
  employeesRangeMax: number | null;
}

interface ICPEmployeeCountChartProps {
  /** Employee count distribution data from API */
  data: EmployeeCountDistributionItem[];
  /** Height of the chart container */
  height?: string;
  /** Whether the chart is loading */
  isLoading?: boolean;
}

/**
 * Component that displays employee count distribution chart with proper employee range formatting
 */
export const ICPEmployeeCountChart: FC<ICPEmployeeCountChartProps> = ({
  data,
  height,
  isLoading = false,
}) => {
  const chartData = useMemo(() => {
    const mappedData = data.map((item) => {
      const range = {
        max: item.employeesRangeMax || Number.POSITIVE_INFINITY,
        min: item.employeesRangeMin || 0,
      };

      return {
        category: getSegmentNameFromRange(range, DEFAULT_API_EMPLOYEE_RANGES),
        value: item.share,
        count: item.count,
        countText: pluralize("company", item.count),
        minValue: item.employeesRangeMin || 0,
      };
    });

    return mappedData.sort((a, b) => a.minValue - b.minValue);
  }, [data]);

  const totalCount = useMemo(() => {
    return data.reduce((sum, item) => sum + item.count, 0);
  }, [data]);

  return (
    <ICPDistributionChart
      title="Employees Distribution"
      data={chartData}
      height={height}
      columnLabelFormat="{valueY}% ({count})"
      columnTooltipFormat="{categoryX}: [bold]{valueY}%[/] ({count} {countText})"
      totalCount={totalCount}
      isLoading={isLoading}
    />
  );
};
